import { Add } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { GridRowId, useGridApiRef } from '@mui/x-data-grid-pro';
import { DEFAULT_CURRENCY } from '@procureai/schemas';
import { CurrencyProvider } from '@procureai/ui-mui';
import { useCallback } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';

import { RefineQuoteDetailsStepFormData } from '../../RefineQuoteDetailsStep.schema';
import { I18nNamespace } from '~/common/types';
import { DataGridProStyled } from '~/components/DataGridCustom';

import { useQuoteLineItemGridConfig } from './QuoteLineItemForm.edit.dataGrid';

type IntakeRequestStepLineItemsFormProps = {
  disabled?: boolean;
};

export const QuoteLineItemForm = ({ disabled }: IntakeRequestStepLineItemsFormProps) => {
  const { t } = useTranslation(I18nNamespace.common, { keyPrefix: 'QuoteLineItemForm' });
  const { fields, remove, append } = useFieldArray<RefineQuoteDetailsStepFormData, 'lineItems'>({
    name: 'lineItems',
    shouldUnregister: true,
  });
  const apiRef = useGridApiRef();

  const getRowIndex = useCallback(
    (rowId: GridRowId) => {
      const rowIds = apiRef.current.getAllRowIds();
      const rowIndex = rowIds.findIndex((rId) => rId === rowId);
      return rowIndex;
    },
    [apiRef],
  );

  const handleRowDelete = useCallback(
    (id: GridRowId) => {
      // update react-hook-form
      const rowIndex = fields.findIndex((row) => row.id === id);

      remove(rowIndex);

      // update Data Grid cache
      apiRef.current.updateRows([{ id, _action: 'delete' }]);
    },
    [apiRef, fields, remove],
  );

  const config = useQuoteLineItemGridConfig({ getRowIndex, onRowDelete: handleRowDelete, disabled });
  const { columnVisibilityModel } = config;

  const handleAddRow = () => {
    append({
      id: v4(),
      position: `${fields.length + 1}`,
      title: '',
      description: '',
      commodityGroupId: null,
      unitId: null,
      quantity: 0,
      pricePerUnit: 0,
      expectedDeliveryFrom: null,
      expectedDeliveryTo: null,
    } as any);
  };
  const { watch } = useFormContext();
  return (
    <>
      <CurrencyProvider value={watch().currency ?? DEFAULT_CURRENCY}>
        <DataGridProStyled
          {...config}
          apiRef={apiRef}
          autoHeight
          hideFooter
          disableColumnMenu
          rows={fields as any} // TODO: Temporary fix until 7bridges schema update is applied
          getRowId={(row) => row.id}
          initialState={{
            pinnedColumns: { right: ['deleteAction'] },
          }}
          sx={{
            '& .MuiDataGrid-row': { cursor: 'default' },
          }}
          columnVisibilityModel={columnVisibilityModel}
          rowSelection={false}
          sortingMode={'server'}
        />
        <Box
          sx={{
            mt: 3,
          }}
        >
          <Button
            disabled={disabled}
            variant="outlined"
            onClick={handleAddRow}
            startIcon={<Add />}
            data-intercom-target="IntakePurchaseRequisition/LineItems/Add"
          >
            {t('addLineItemButton')}
          </Button>
        </Box>
      </CurrencyProvider>
    </>
  );
};
